import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const statuses = ["open", "assigned", "in_progress", "awaiting_tenant", "resolved", "closed", "escalated"];
const statusColors = {
  open: "bg-red-50 text-red-700",
  assigned: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  awaiting_tenant: "bg-purple-50 text-purple-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-gray-100 text-gray-500",
  escalated: "bg-red-100 text-red-800",
};
const severityColors = {
  low: "", medium: "border-amber-300 text-amber-700", high: "border-orange-300 text-orange-700", urgent: "border-red-300 text-red-700",
};

export default function AdminMaintenancePage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const fetchRequests = (status) => {
    setLoading(true);
    const params = status && status !== "all" ? { status } : {};
    api.get("/admin/maintenance", { params })
      .then((r) => setRequests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRequests(tab); }, [tab]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/maintenance/${id}/status`, { status });
      toast.success(`Status updated to ${status.replace(/_/g, " ")}`);
      fetchRequests(tab);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Maintenance Management">
      <div className="space-y-6" data-testid="admin-maintenance-page">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="open" className="text-xs">Open</TabsTrigger>
            <TabsTrigger value="assigned" className="text-xs">Assigned</TabsTrigger>
            <TabsTrigger value="in_progress" className="text-xs">In Progress</TabsTrigger>
            <TabsTrigger value="resolved" className="text-xs">Resolved</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No maintenance requests found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r) => (
                    <TableRow key={r._id} data-testid={`maint-row-${r._id}`}>
                      <TableCell className="font-medium">{r.user_name}</TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={severityColors[r.severity]}>{r.severity}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm">{r.description}</TableCell>
                      <TableCell><Badge className={statusColors[r.status]}>{r.status?.replace(/_/g, " ")}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Select onValueChange={(v) => updateStatus(r._id, v)}>
                          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`maint-status-${r._id}`}>
                            <SelectValue placeholder="Update" />
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.filter((s) => s !== r.status).map((s) => (
                              <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
